import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, STUDIO } from "../lib/store";
import { storeImage, storeFile, storeFileSmart } from "../lib/sync";
import { fileToDataUri } from "../lib/image";
import { draftConcept, rewriteSlide } from "../lib/ai";
import { conceptContext } from "../lib/snapshot";
import { rasterisePages, renderPageAt, type RasterPage } from "../lib/pdfRender";
import { detectCells, annotateCells, cropCell, type Cell } from "../lib/boardCrop";
import { relative } from "../lib/format";
import { checkClaims, deckTexts } from "../lib/metricCheck";
import { FilmFrame } from "../components/FilmFrame";
import type { Project, DeckSlide, DesignOption, SlideKind } from "../lib/types";

/* Crafting the concept presentation: the narrative you walk the client through,
   and the options you're asking them to choose between. The assistant drafts
   from the project's own record; the architect edits, because it's the
   architect's design and the architect's voice. */

/** A PDF or text file must reach the model byte-for-byte — no canvas re-encode. */
const rawDataUri = (file: File) => new Promise<string>((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result as string);
  r.onerror = rej;
  r.readAsDataURL(file);
});

/* The presentations a project actually goes through. A studio can type its own
   — these are just the usual ones, offered so nobody has to. */
const DECK_STAGES = ["Concept Design", "Developed Design", "Technical Design", "Tender", "Construction"];
/* A render presentation is a different animal: images carry it, there is nothing
   to choose between, and the client reacts to how it looks rather than deciding
   between approaches. */
const DECK_KINDS: { key: "concept" | "renders" | "design"; label: string; blurb: string }[] = [
  { key: "concept", label: "A concept presentation", blurb: "The thinking, the drawings, and options to choose between." },
  { key: "design", label: "A design proposal", blurb: "The resolved scheme, walked through space by space — drafted from your drawings." },
  { key: "renders", label: "A render presentation", blurb: "The visuals, full-bleed, with somewhere to say how each one feels." },
];

const KIND_LABEL: Record<SlideKind, string> = {
  cover: "Cover", film: "Film", statement: "Statement", image: "Image", spaces: "Accommodation", palette: "Materials", options: "The options", closing: "Closing",
};

export function DeckBuilder({ p, by }: { p: Project; by: string }) {
  const deck = p.deck;
  const [draftOpen, setDraftOpen] = useState(false);
  const [present, setPresent] = useState<number | null>(null);
  const [editing, setEditing] = useState<DeckSlide | null>(null);
  const [optEditing, setOptEditing] = useState<DesignOption | null>(null);
  const [stage, setStage] = useState(DECK_STAGES[0]);
  const [newDeck, setNewDeck] = useState(false);
  const [kind, setKind] = useState<"concept" | "renders" | "design">("concept");

  if (!deck) {
    return (
      <div className="card pad" style={{ maxWidth: 620 }}>
        <div className="kicker">No concept presentation yet</div>
        <h3 className="serif" style={{ fontSize: 23, marginTop: 6 }}>Craft the design concept</h3>
        <p className="muted" style={{ fontSize: 14, marginTop: 8, lineHeight: 1.65 }}>
          Build the presentation you'll walk the client through — the thinking, the drawings, and the options you want
          them to choose between. The assistant can draft the narrative from the brief, the accommodation and the mood
          board; you edit it into your own voice.
        </p>
        <div className="field" style={{ marginTop: 16 }}>
          <label>What sort of presentation is this?</label>
          <div className="col" style={{ gap: 6 }}>
            {DECK_KINDS.map((k) => (
              <button key={k.key} onClick={() => setKind(k.key)} className="panel row between"
                style={{ padding: "9px 12px", cursor: "pointer", textAlign: "left", borderColor: kind === k.key ? "var(--accent)" : "var(--line)" }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 500 }}>{k.label}</span>
                  <span className="dim" style={{ display: "block", fontSize: 12, marginTop: 1 }}>{k.blurb}</span>
                </span>
                {kind === k.key && <Icon.check width={14} style={{ color: "var(--accent)", flexShrink: 0 }} />}
              </button>
            ))}
          </div>
        </div>
        <div className="field" style={{ marginTop: 14 }}>
          <label>Which stage is this presentation for?</label>
          <div className="row wrap" style={{ gap: 6 }}>
            {DECK_STAGES.map((st) => (
              <button key={st} className="chip" onClick={() => setStage(st)}
                style={{ fontSize: 12, padding: "6px 12px", background: stage === st ? "var(--accent)" : "var(--card)", color: stage === st ? "#fff" : "var(--ink-2)", borderColor: stage === st ? "var(--accent)" : "var(--line-2)" }}>{st}</button>
            ))}
          </div>
        </div>
        <div className="row wrap" style={{ gap: 10, marginTop: 16 }}>
          {kind !== "renders"
            ? <button className="btn accent" onClick={() => { actions.startDeck(p.id, stage, by, kind); setDraftOpen(true); }}><Icon.spark width={15} />
                {kind === "design" ? "Draft the proposal with AI" : "Draft it with AI"}
              </button>
            : null}
          <button className={kind === "renders" ? "btn accent" : "btn ghost"} onClick={() => actions.startDeck(p.id, stage, by, kind)}>
            <Icon.plus width={15} />{kind === "renders" ? "Start the render presentation" : "Start empty"}
          </button>
        </div>
        {(p.deckArchive ?? []).length > 0 && <PastDecks p={p} by={by} />}

      {draftOpen && <DraftModal p={p} by={by} onClose={() => setDraftOpen(false)} />}
      </div>
    );
  }

  const issued = deck.status === "issued";
  const chosen = deck.options.find((o) => o.response?.choice === "preferred");
  const clientNotes = (deck.notes ?? []).filter((n) => n.author === "client");
  /* The figures are copied verbatim and trustworthy; the comparisons between them
     are the assistant's arithmetic, and arithmetic can be checked. */
  const claimIssues = checkClaims(deck.options, deckTexts(deck));

  return (
    <div className="col" style={{ gap: 18 }}>
      {/* status + actions */}
      <div className="card pad">
        <div className="row between wrap" style={{ gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="row" style={{ gap: 8, alignItems: "baseline" }}>
              <h3 className="serif" style={{ fontSize: 22 }}>{deck.title}</h3>
              {deck.stage && <span className="badge accent" style={{ fontSize: 9 }}>{deck.stage}</span>}
              {issued ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />Presented</span> : <span className="badge" style={{ fontSize: 9 }}>Draft</span>}
            </div>
            <div className="dim" style={{ fontSize: 12.5, marginTop: 4 }}>
              {deck.slides.length} slide{deck.slides.length === 1 ? "" : "s"} · {deck.options.length} option{deck.options.length === 1 ? "" : "s"}
              {issued && deck.issuedOn ? ` · presented ${relative(deck.issuedOn)}` : ` · edited ${relative(deck.updatedOn)}`}
            </div>
          </div>
          <div className="row wrap" style={{ gap: 8 }}>
            {deck.kind !== "renders" && <button className="btn ghost sm" onClick={() => setDraftOpen(true)}><Icon.spark width={14} />{deck.slides.length > 1 ? "Re-draft with AI" : "Draft with AI"}</button>}
            <button className="btn soft sm" disabled={!deck.slides.length} onClick={() => setPresent(0)}><Icon.present width={14} />Present</button>
            <button className="btn accent sm" onClick={() => {
              const who = p.client?.name?.split(" ")[0] ?? "the client";
              const what = `This publishes the concept to ${who}'s portal and emails them a link.\n\nThey can watch the film, read it through, mark the option they want developed with a comment, and comment on any slide — all in the portal.`;
              const warn = claimIssues.length
                ? `${claimIssues.length} comparison${claimIssues.length === 1 ? "" : "s"} in this presentation may contradict the figures printed on your sheets.\n\n${what}\n\nSend it anyway?`
                : issued ? `Re-send the concept?\n\n${what}` : `${what}\n\nSend it?`;
              if (confirm(warn)) actions.issueDeck(p.id, by);
            }}>
              <Icon.send width={14} />{issued ? "Re-send to the portal" : "Send to the client portal"}
            </button>
            <button className="btn ghost sm" title="Close this one and start a different presentation"
              onClick={() => setNewDeck(true)}><Icon.plus width={13} />New presentation</button>
            {issued && (
              <button className="btn ghost sm" title="Close this presentation and start one for the next stage"
                onClick={() => { if (confirm("Close this presentation?\n\nThe client keeps it and their responses — it just stops being the current one, and you can start the next stage.")) actions.closeDeck(p.id, by); }}>
                <Icon.check width={13} />Close it
              </button>
            )}
          </div>
        </div>
        {/* Fire-and-forget email used to hide a failure behind a green tick. The
            outcome is recorded now, and a failure says so with a way to retry. */}
        {issued && deck.notify && deck.notify.result !== "delivered" && (
          <div className="panel" style={{ padding: "11px 13px", marginTop: 12, borderLeft: "3px solid var(--danger)" }}>
            <div className="row between wrap" style={{ gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <b style={{ fontSize: 13.5, color: "var(--danger)" }}>The client was not emailed</b>
                <div className="dim" style={{ fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>
                  It's published to their portal, but the notification{" "}
                  {deck.notify.result === "simulated"
                    ? "was only simulated — email isn't switched on for this worker"
                    : `didn't send${deck.notify.error ? ` — ${deck.notify.error}` : ""}`}.
                  {!p.client?.email?.trim() && " There's no email address on file for this client — add one in Project data."}
                </div>
              </div>
              <button className="btn ghost sm" onClick={() => {
                const to = p.client?.email?.trim() || (prompt("Send it to which address?") ?? "").trim();
                if (to) actions.resendDeckEmail(p.id, to);
              }}><Icon.send width={13} />Try again</button>
            </div>
          </div>
        )}
        {issued && deck.notify?.result === "delivered" && (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
            <Icon.check width={10} style={{ color: "var(--ok)", verticalAlign: "-1px", marginRight: 4 }} />
            Emailed to {deck.notify.to} · {relative(deck.notify.on)}
          </div>
        )}
        {claimIssues.length > 0 && (
          <div className="panel" style={{ padding: "11px 13px", marginTop: 12, borderLeft: "3px solid var(--danger)" }}>
            <div className="row" style={{ gap: 8 }}>
              <b style={{ fontSize: 13.5, color: "var(--danger)" }}>
                {claimIssues.length} comparison{claimIssues.length === 1 ? "" : "s"} worth checking against the sheets
              </b>
            </div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 3, lineHeight: 1.5 }}>
              The numbers are read straight off your sheets and are reliable; the ranking between them is the assistant's arithmetic. Each of these looks wrong — read them before
              this goes out, or a client with a calculator will.
            </div>
            <div className="col" style={{ gap: 7, marginTop: 9 }}>
              {claimIssues.slice(0, 6).map((c, i) => (
                <div key={i} style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                  <span className="badge" style={{ fontSize: 8.5 }}>{c.where}</span>
                  <div style={{ marginTop: 3 }}>“{c.sentence}”</div>
                  <div style={{ color: "var(--danger)", marginTop: 1 }}>— but {c.truth}</div>
                </div>
              ))}
              {claimIssues.length > 6 && <div className="dim" style={{ fontSize: 11.5 }}>…and {claimIssues.length - 6} more.</div>}
            </div>
          </div>
        )}
        {deck.acceptance && (
          <div className="panel" style={{ padding: "10px 13px", marginTop: 12, borderLeft: deck.acceptance.status === "accepted" ? "3px solid var(--ok)" : "3px solid var(--warn)" }}>
            <div className="row" style={{ gap: 8 }}>
              <Icon.check width={14} style={{ color: deck.acceptance.status === "accepted" ? "var(--ok)" : "var(--warn)" }} />
              <b style={{ fontSize: 13.5 }}>
                {deck.acceptance.status === "accepted" ? "The client accepted this presentation" : "The client asked for changes"}
              </b>
            </div>
            {deck.acceptance.comment && <div style={{ fontSize: 13, marginTop: 5, lineHeight: 1.55 }}>“{deck.acceptance.comment}”</div>}
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>{deck.acceptance.by} · {relative(deck.acceptance.on)}</div>
          </div>
        )}
        {chosen && (
          <div className="panel" style={{ padding: "10px 13px", marginTop: 12, borderLeft: "3px solid var(--ok)" }}>
            <div className="row" style={{ gap: 8 }}><Icon.check width={14} style={{ color: "var(--ok)" }} /><b style={{ fontSize: 13.5 }}>The client prefers {chosen.name}</b></div>
            {chosen.response?.comment && <div style={{ fontSize: 13, marginTop: 5, lineHeight: 1.55 }}>“{chosen.response.comment}”</div>}
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>{chosen.response?.by} · {relative(chosen.response!.on)}</div>
          </div>
        )}
      </div>

      {/* the options — a render presentation has nothing to choose between */}
      {deck.kind === "concept" && <div className="card" style={{ overflow: "hidden" }}>
        <div className="row between wrap" style={{ padding: "13px 18px", borderBottom: deck.options.length ? "1px solid var(--line)" : "none", gap: 10 }}>
          <span className="row" style={{ gap: 8 }}><Icon.layers width={15} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Design options</span></span>
          <button className="btn ghost sm" onClick={() => actions.addOption(p.id)}><Icon.plus width={13} />Add an option</button>
        </div>
        {deck.options.length === 0 ? (
          <div className="dim" style={{ padding: "16px 18px", fontSize: 13, lineHeight: 1.6 }}>
            Give the client a real choice — two or three genuinely different approaches, each with what it gives up. One option isn't a choice, and an option with no trade-off never survives contact with the budget.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 12, padding: 14 }}>
            {deck.options.map((o) => (
              <div key={o.id} className="panel" style={{ padding: 0, overflow: "hidden", borderColor: o.response?.choice === "preferred" ? "var(--ok)" : "var(--line)" }}>
                {o.images[0]
                  ? <img src={o.images[0]} alt={o.name} style={{ width: "100%", height: 130, objectFit: "cover", display: "block" }} />
                  : <div style={{ height: 130, background: "var(--paper-2)", display: "grid", placeItems: "center", color: "var(--ink-3)", fontSize: 12 }}>No image yet</div>}
                <div style={{ padding: "11px 13px" }}>
                  <div className="row between" style={{ gap: 8 }}>
                    <span className="row" style={{ gap: 7, alignItems: "baseline", minWidth: 0 }}>
                      <span className="badge accent mono" style={{ fontSize: 10 }}>{o.ref}</span>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{o.name}</span>
                    </span>
                    {o.response && <span className="badge" style={{ fontSize: 8.5, color: o.response.choice === "preferred" ? "var(--ok)" : o.response.choice === "no" ? "var(--danger)" : "var(--warn)" }}>{o.response.choice}</span>}
                  </div>
                  {o.boardRef && <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>{o.boardRef}{o.page ? ` · page ${o.page}` : ""}</div>}
                  {o.thesis && <div style={{ fontSize: 12.5, marginTop: 5, lineHeight: 1.5, fontStyle: "italic", color: "var(--ink-2)" }}>{o.thesis}</div>}
                  {(o.metrics ?? []).length > 0 && (
                    <div className="col" style={{ gap: 2, marginTop: 8 }}>
                      {o.metrics!.map((m, k) => (
                        <div key={k} className="row between" style={{ gap: 8, fontSize: 11.5 }}>
                          <span className="dim" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.label}</span>
                          <span className="mono" style={{ flexShrink: 0 }}>{m.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="row wrap" style={{ gap: 10, marginTop: 8, fontSize: 11 }}>
                    {(o.decisions ?? []).length > 0 && <span className="dim">{o.decisions!.length} decision{o.decisions!.length === 1 ? "" : "s"} narrated</span>}
                    <span className="dim">{o.strengths.length} strength{o.strengths.length === 1 ? "" : "s"}</span>
                    <span className="dim">{o.tradeoffs.length} trade-off{o.tradeoffs.length === 1 ? "" : "s"}</span>
                    {o.images.length > 1 && <span className="dim">{o.images.length} images</span>}
                  </div>
                  <div className="row" style={{ gap: 6, marginTop: 10 }}>
                    <button className="chip" style={{ fontSize: 11 }} onClick={() => setOptEditing(o)}>Edit</button>
                    <span style={{ flex: 1 }} />
                    <button className="iconbtn" style={{ width: 26, height: 26 }} onClick={() => { if (confirm(`Remove ${o.name}?`)) actions.removeOption(p.id, o.id); }}><Icon.close width={12} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>}

      {/* the slides */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div className="row between" style={{ padding: "13px 18px", borderBottom: "1px solid var(--line)" }}>
          <span className="row" style={{ gap: 8 }}><Icon.present width={15} style={{ color: "var(--accent-ink)" }} /><span className="kicker">The narrative</span></span>
          <span className="dim" style={{ fontSize: 12 }}>{deck.slides.length} slide{deck.slides.length === 1 ? "" : "s"}</span>
        </div>
        {deck.slides.map((s, i) => (
          <div key={s.id} className="row between wrap" style={{ padding: "11px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 12 }}>
            <span className="row" style={{ gap: 12, minWidth: 0, flex: 1 }}>
              <span className="mono dim" style={{ fontSize: 11, width: 18, flexShrink: 0 }}>{i + 1}</span>
              {s.video
                ? <span title={s.videoName} style={{ width: 54, height: 38, borderRadius: 4, background: "#1b1b1d", color: "#fff", flexShrink: 0, display: "grid", placeItems: "center" }}><Icon.present width={13} /></span>
                : s.images?.[0]
                ? <img src={s.images[0]} alt="" style={{ width: 54, height: 38, objectFit: "cover", borderRadius: 4, flexShrink: 0, border: "1px solid var(--line)" }} />
                : <span style={{ width: 54, height: 38, borderRadius: 4, background: "var(--paper-2)", flexShrink: 0 }} />}
              <span style={{ minWidth: 0 }}>
                <span className="row" style={{ gap: 7, alignItems: "baseline" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 500 }}>{s.title || "Untitled"}</span>
                  <span className="badge" style={{ fontSize: 8 }}>{KIND_LABEL[s.kind]}</span>
                  {s.aiDrafted && <span className="badge" style={{ fontSize: 8, color: "var(--accent-ink)" }}>AI draft</span>}
                </span>
                {s.body && <span className="dim" style={{ display: "block", fontSize: 11.5, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 460 }}>{s.body}</span>}
                {s.source && (
                  <span title={`From the document${s.source.where ? ` — ${s.source.where}` : ""}: “${s.source.quote}”`}
                    style={{ display: "block", fontSize: 11, marginTop: 3, color: "var(--accent-ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 460 }}>
                    <Icon.chat width={9} style={{ verticalAlign: "-1px", marginRight: 4 }} />{s.source.quote}{s.source.where ? ` — ${s.source.where}` : ""}
                  </span>
                )}
              </span>
            </span>
            <span className="row" style={{ gap: 4, flexShrink: 0 }}>
              <button className="iconbtn" style={{ width: 26, height: 26 }} title="Up" disabled={i === 0} onClick={() => actions.moveSlide(p.id, s.id, -1)}><Icon.caretUp width={12} /></button>
              <button className="iconbtn" style={{ width: 26, height: 26 }} title="Down" disabled={i === deck.slides.length - 1} onClick={() => actions.moveSlide(p.id, s.id, 1)}><Icon.caretDown width={12} /></button>
              <button className="chip" style={{ fontSize: 11 }} onClick={() => setEditing(s)}>Edit</button>
              <button className="iconbtn" style={{ width: 26, height: 26 }} title="Remove" onClick={() => { if (confirm("Remove this slide?")) actions.removeSlide(p.id, s.id); }}><Icon.close width={12} /></button>
            </span>
          </div>
        ))}
        {/* The film opens the presentation. The client meets the thinking in
            motion, then goes into the drawings with the decisions narrated. */}
        {(p.films ?? []).length > 0 && (
          <div className="row wrap between" style={{ gap: 10, padding: "11px 18px", borderTop: "1px solid var(--line)", background: "var(--paper-2)" }}>
            <span className="row wrap" style={{ gap: 8, alignItems: "center" }}>
              <Icon.present width={14} style={{ color: "var(--accent-ink)" }} />
              <span style={{ fontSize: 13 }}>Open with a film</span>
              <select className="input" style={{ fontSize: 12.5, padding: "4px 8px", width: "auto" }}
                value={deck.slides.find((s) => s.kind === "film")?.filmId ?? ""}
                onChange={(e) => actions.setOpeningFilm(p.id, e.target.value || null, by)}>
                <option value="">No film</option>
                {(p.films ?? []).map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
              </select>
            </span>
            <span className="dim" style={{ fontSize: 11.5, maxWidth: 380, lineHeight: 1.5 }}>
              It plays first, before the drawings — then they go into the scheme with the decisions narrated.
            </span>
          </div>
        )}
        <AddRenders p={p} />
        <div className="row wrap" style={{ gap: 6, padding: "12px 18px", borderTop: "1px solid var(--line)" }}>
          <span className="dim" style={{ fontSize: 12, marginRight: 4 }}>Add:</span>
          {(["statement", "image", "spaces", "palette", "options", "closing"] as SlideKind[]).map((k) => (
            <button key={k} className="chip" style={{ fontSize: 11 }} onClick={() => actions.addSlide(p.id, k)}>{KIND_LABEL[k]}</button>
          ))}
        </div>
      </div>

      {/* what it took out of the client's own document */}
      {(deck.sources ?? []).length > 0 && <SourcesPanel p={p} by={by} />}

      {/* what the client said */}
      {clientNotes.length > 0 && (
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="row between" style={{ padding: "13px 18px", borderBottom: "1px solid var(--line)" }}>
            <span className="kicker">The client's comments</span><span className="dim" style={{ fontSize: 12 }}>{clientNotes.length}</span>
          </div>
          {clientNotes.map((n, i) => {
            const slide = deck.slides.find((s) => s.id === n.slideId);
            const opt = deck.options.find((o) => o.id === n.optionId);
            return (
              <div key={n.id} style={{ padding: "12px 18px", borderTop: i ? "1px solid var(--line)" : "none" }}>
                <div className="row between wrap" style={{ gap: 8 }}>
                  <span className="row" style={{ gap: 8 }}>
                    <b style={{ fontSize: 13 }}>{n.by}</b>
                    {slide && <span className="badge" style={{ fontSize: 8.5 }}>{slide.title}</span>}
                    {opt && <span className="badge accent" style={{ fontSize: 8.5 }}>{opt.name}</span>}
                  </span>
                  <span className="dim" style={{ fontSize: 11.5 }}>{relative(n.on)}</span>
                </div>
                <div style={{ fontSize: 13.5, marginTop: 5, lineHeight: 1.55 }}>{n.body}</div>
                {n.reply
                  ? <div className="panel" style={{ padding: "8px 11px", marginTop: 8 }}><div className="kicker" style={{ fontSize: 8 }}>You replied</div><div style={{ fontSize: 13, marginTop: 3 }}>{n.reply.body}</div></div>
                  : <ReplyBox onSend={(v) => actions.replyDeckNote(p.id, n.id, v, by)} />}
              </div>
            );
          })}
        </div>
      )}

      {(p.deckArchive ?? []).length > 0 && <PastDecks p={p} by={by} />}

      {newDeck && <NewDeckModal p={p} by={by} onClose={() => setNewDeck(false)} onDraft={() => setDraftOpen(true)} />}
      {draftOpen && <DraftModal p={p} by={by} onClose={() => setDraftOpen(false)} />}
      {editing && <SlideModal p={p} slide={editing} onClose={() => setEditing(null)} />}
      {optEditing && <OptionModal p={p} option={optEditing} onClose={() => setOptEditing(null)} />}
      {present !== null && <Presenter p={p} start={present} onClose={() => setPresent(null)} />}
    </div>
  );
}

/* ---- what the assistant lifted out of the client's document ----
   Provenance, not decoration: the architect reads the quote, decides whether the
   assistant understood it, and turns anything it under-used into its own slide. */
function SourcesPanel({ p, by }: { p: Project; by: string }) {
  const deck = p.deck!;
  const sources = deck.sources ?? [];
  const [open, setOpen] = useState(true);
  const used = sources.filter((s) => s.usedFor).length;

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <button onClick={() => setOpen((v) => !v)}
        style={{ width: "100%", background: "none", border: "none", padding: "13px 18px", cursor: "pointer", textAlign: "left", borderBottom: open ? "1px solid var(--line)" : "none" }}>
        <span className="row between" style={{ gap: 10 }}>
          <span className="row" style={{ gap: 8 }}>
            <Icon.brief width={15} style={{ color: "var(--accent-ink)" }} />
            <span className="kicker">From the client's document</span>
          </span>
          <span className="row" style={{ gap: 10 }}>
            <span className="dim" style={{ fontSize: 12 }}>{sources.length} passage{sources.length === 1 ? "" : "s"}{used < sources.length ? ` · ${sources.length - used} unused` : ""}</span>
            {open ? <Icon.caretUp width={12} /> : <Icon.caretDown width={12} />}
          </span>
        </span>
      </button>
      {open && (
        <>
          {(deck.readFiles ?? []).length > 0 && (
            <div className="row wrap" style={{ gap: 6, padding: "10px 18px 0" }}>
              {deck.readFiles!.map((f) => <span key={f} className="badge" style={{ fontSize: 9 }}><Icon.brief width={9} />{f}</span>)}
            </div>
          )}
          <div className="dim" style={{ fontSize: 11.5, padding: "8px 18px 0", lineHeight: 1.55 }}>
            The passages the assistant actually used, in the client's own words. Check them against the slides — anything
            it read wrongly, or left on the table, is yours to correct.
          </div>
          {sources.map((s, i) => (
            <div key={s.id} style={{ padding: "12px 18px", borderTop: i ? "1px solid var(--line)" : "none", marginTop: i ? 0 : 10 }}>
              <div className="row between wrap" style={{ gap: 8 }}>
                <span className="row wrap" style={{ gap: 6 }}>
                  {s.topic && <span className="badge accent" style={{ fontSize: 8.5 }}>{s.topic}</span>}
                  {s.where && <span className="badge" style={{ fontSize: 8.5 }}>{s.where}</span>}
                  {s.file && (deck.readFiles ?? []).length > 1 && <span className="badge" style={{ fontSize: 8.5 }}>{s.file}</span>}
                </span>
                <span className="dim" style={{ fontSize: 11 }}>{s.usedFor ? `→ ${s.usedFor}` : "not used yet"}</span>
              </div>
              <blockquote className="serif" style={{ margin: "7px 0 0", paddingLeft: 12, borderLeft: "2px solid var(--accent)", fontSize: 14.5, lineHeight: 1.6, fontStyle: "italic", color: "var(--ink-2)" }}>
                “{s.quote}”
              </blockquote>
              {!s.usedFor && (
                <button className="chip" style={{ fontSize: 11, marginTop: 8 }} onClick={() => actions.slideFromSource(p.id, s.id, by)}>
                  <Icon.plus width={11} />Make this a slide
                </button>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}


function ReplyBox({ onSend }: { onSend: (v: string) => void }) {
  const [v, setV] = useState("");
  return (
    <div className="row" style={{ gap: 8, marginTop: 8 }}>
      <input className="input" style={{ fontSize: 12.5, padding: "5px 9px" }} value={v} onChange={(e) => setV(e.target.value)} placeholder="Reply…"
        onKeyDown={(e) => { if (e.key === "Enter" && v.trim()) { onSend(v.trim()); setV(""); } }} />
      <button className="btn ghost sm" disabled={!v.trim()} onClick={() => { onSend(v.trim()); setV(""); }}><Icon.send width={12} /></button>
    </div>
  );
}

/* ---- renders come in batches ---- */
function AddRenders({ p }: { p: Project }) {
  const [busy, setBusy] = useState(0);
  const [pct, setPct] = useState<number | null>(null);
  async function take(list: FileList | null) {
    if (!list?.length) return;
    const files = Array.from(list);
    setBusy(files.length);
    const done: { url: string; title: string; video?: boolean; name?: string }[] = [];
    for (const f of files) {
      const title = f.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ");
      try {
        if (/^video\//.test(f.type)) {
          /* Straight to R2, byte for byte, multipart when it is large.
             Re-encoding a flythrough would defeat the point of full quality. */
          setPct(0);
          const { url } = await storeFileSmart(f, (fr) => setPct(Math.round(fr * 100)));
          done.push({ url, title, video: true, name: f.name });
          setPct(null);
        } else {
          const uri = await fileToDataUri(f, 2600, 0.9);
          let url = uri;
          try { const blob = await (await fetch(uri)).blob(); url = (await storeFile(new File([blob], f.name, { type: blob.type || "image/jpeg" }), p.client?.code)).url; }
          catch { try { url = await storeImage(uri, p.client?.code); } catch { /* inline */ } }
          done.push({ url, title });
        }
      } catch { setPct(null); /* skip the one that failed */ }
      setBusy((n) => n - 1);
    }
    if (done.length) actions.addRenderSlides(p.id, done);
    setBusy(0);
  }
  return (
    <div className="row wrap" style={{ gap: 10, alignItems: "center", padding: "12px 18px", borderTop: "1px solid var(--line)", background: "var(--paper-2)" }}>
      <label className="btn accent sm" style={{ cursor: busy ? "default" : "pointer" }}>
        <Icon.image width={13} />{pct != null ? `Uploading ${pct}%` : busy ? `Adding ${busy}…` : "Add renders or video"}
        <input type="file" accept="image/*,video/*" multiple hidden disabled={!!busy} onChange={(e) => { void take(e.target.files); e.currentTarget.value = ""; }} />
      </label>
      <span className="dim" style={{ fontSize: 11.5, lineHeight: 1.5, flex: 1, minWidth: 200 }}>
        Each image or video becomes a full-bleed slide, named from the file. Video goes up at full quality, untouched.
      </span>
    </div>
  );
}

/* ---- starting a different presentation ----
   A live deck used to be a dead end: the kind picker only appeared when a
   project had no presentation at all, and closing was only offered once one had
   been issued. So a project sitting on a draft concept could never start a
   design proposal. This is the way through. */
function NewDeckModal({ p, by, onClose, onDraft }: { p: Project; by: string; onClose: () => void; onDraft: () => void }) {
  const [kind, setKind] = useState<"concept" | "renders" | "design">("design");
  const [stage, setStage] = useState(DECK_STAGES[1] ?? DECK_STAGES[0]);
  const live = p.deck;

  function start(draft: boolean) {
    actions.startDeck(p.id, stage, by, kind);
    onClose();
    if (draft) onDraft();
  }

  return (
    <Modal open onClose={onClose} title="Start a different presentation" wide>
      <div className="col" style={{ gap: 14 }}>
        {live && (
          <div className="panel" style={{ padding: "10px 13px", borderLeft: "3px solid var(--warn)" }}>
            <b style={{ fontSize: 13 }}>“{live.title}” will be closed.</b>
            <div className="dim" style={{ fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>
              It moves to Earlier presentations — the client keeps it and anything they said. You can make it current again later.
            </div>
          </div>
        )}
        <div className="field">
          <label>What sort of presentation is this?</label>
          <div className="col" style={{ gap: 6 }}>
            {DECK_KINDS.map((k) => (
              <button key={k.key} onClick={() => setKind(k.key)} className="panel row between"
                style={{ padding: "9px 12px", cursor: "pointer", textAlign: "left", borderColor: kind === k.key ? "var(--accent)" : "var(--line)" }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 500 }}>{k.label}</span>
                  <span className="dim" style={{ display: "block", fontSize: 12, marginTop: 1 }}>{k.blurb}</span>
                </span>
                {kind === k.key && <Icon.check width={14} style={{ color: "var(--accent)", flexShrink: 0 }} />}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label>Which stage is it for?</label>
          <div className="row wrap" style={{ gap: 6 }}>
            {DECK_STAGES.map((st) => (
              <button key={st} className="chip" onClick={() => setStage(st)}
                style={{ fontSize: 12, padding: "6px 12px", background: stage === st ? "var(--accent)" : "var(--card)", color: stage === st ? "#fff" : "var(--ink-2)", borderColor: stage === st ? "var(--accent)" : "var(--line-2)" }}>{st}</button>
            ))}
          </div>
        </div>
        <div className="row wrap" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn ghost" onClick={() => start(false)}><Icon.plus width={15} />Start empty</button>
          {kind !== "renders" && (
            <button className="btn accent" onClick={() => start(true)}>
              <Icon.spark width={15} />{kind === "design" ? "Start and draft the proposal" : "Start and draft it"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ---- what came before ----
   A project is presented more than once. The earlier decks stay readable, with
   whatever the client said at the time, and any of them can be made live again
   if one was closed too soon. */
function PastDecks({ p, by }: { p: Project; by: string }) {
  const past = p.deckArchive ?? [];
  return (
    <div className="card" style={{ overflow: "hidden", marginTop: 18 }}>
      <div className="row between" style={{ padding: "13px 18px", borderBottom: "1px solid var(--line)" }}>
        <span className="kicker">Earlier presentations</span>
        <span className="dim" style={{ fontSize: 12 }}>{past.length}</span>
      </div>
      {past.map((d, i) => {
        const chosen = d.options.find((o) => o.response?.choice === "preferred");
        return (
          <div key={d.id} className="row between wrap" style={{ padding: "11px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
            <span style={{ minWidth: 0 }}>
              <span className="row wrap" style={{ gap: 7, alignItems: "baseline" }}>
                {d.stage && <span className="badge" style={{ fontSize: 8.5 }}>{d.stage}</span>}
                <span style={{ fontSize: 13.5, fontWeight: 500 }}>{d.title}</span>
              </span>
              <span className="dim" style={{ display: "block", fontSize: 11.5, marginTop: 2 }}>
                {d.slides.length} slides · {d.options.length} options
                {chosen ? ` · they chose ${chosen.name}` : ""}
                {d.closedOn ? ` · closed ${relative(d.closedOn)}` : ""}
              </span>
            </span>
            <button className="chip" style={{ fontSize: 11 }}
              onClick={() => { if (confirm(`Make "${d.title}" the current presentation again?\n\nThe live one is closed and kept.`)) actions.reopenDeck(p.id, d.id, by); }}>
              Make current again
            </button>
          </div>
        );
      })}
    </div>
  );
}

/* ---- AI draft ---- */
/* Two different limits, because the two files travel very differently.
   A board set is rasterised HERE and only the rendered images are sent, so its
   size is bounded by what the browser can open — not by any upload. A supporting
   document is posted whole, base64'd, so it has a real ceiling. */
const MAX_BOARD_MB = 400;
const MAX_DOC_MB = 24;

function DraftModal({ p, by, onClose }: { p: Project; by: string; onClose: () => void }) {
  /* Two jobs, and they are not the same job.
     "present" — the design is drawn. The boards go up, the assistant reads them
     and narrates what the architect did. It must not invent a scheme.
     "develop" — there is no design yet, and the assistant helps shape one. */
  /* A design-proposal deck always drafts as a proposal; a concept deck can
     either present drawn options or help develop new ones. */
  const isProposal = p.deck?.kind === "design";
  const [mode, setMode] = useState<"present" | "develop" | "design">(isProposal ? "design" : "present");
  const [count, setCount] = useState(3);
  const [instruction, setInstruction] = useState("");
  const [docs, setDocs] = useState<{ name: string; dataUri: string; size: number }[]>([]);
  const [boardFile, setBoardFile] = useState<File | null>(null);
  const [boards, setBoards] = useState<RasterPage[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [stage, setStage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const hasDeck = (p.deck?.slides.length ?? 0) > 1;
  const presenting = mode === "present" || mode === "design";

  /* ---- the drawn set ---- */
  async function takeBoards(file: File | null) {
    if (!file) return;
    setErr(null);
    if (file.size > MAX_BOARD_MB * 1024 * 1024) { setErr(`${file.name} is ${(file.size / 1024 / 1024).toFixed(0)} MB — too large for the browser to open. Export the boards without the embedded originals.`); return; }
    setBusy(true); setStage("Opening the boards…");
    try {
      const pages = await rasterisePages(file, 460);
      setBoardFile(file);
      setBoards(pages);
      setPicked(new Set(pages.map((x) => x.page)));   // present all of them unless told otherwise
    } catch { setErr(`Couldn't open ${file.name} — is it a PDF?`); }
    setBusy(false); setStage(null);
  }

  /* ---- supporting reading (a brief, a proposal) ---- */
  async function addDocs(list: FileList | null) {
    if (!list?.length) return;
    setErr(null);
    const next: { name: string; dataUri: string; size: number }[] = [];
    for (const f of Array.from(list).slice(0, 4)) {
      if (f.size > MAX_DOC_MB * 1024 * 1024) { setErr(`${f.name} is ${(f.size / 1024 / 1024).toFixed(0)} MB. A supporting document is sent whole, so it has to stay under ${MAX_DOC_MB} MB — split it, or attach it as boards instead.`); continue; }
      try {
        const uri = /^image\//.test(f.type) ? await fileToDataUri(f, 2200, 0.85) : await rawDataUri(f);
        next.push({ name: f.name, dataUri: uri, size: f.size });
      } catch { setErr(`Couldn't read ${f.name}.`); }
    }
    setDocs((x) => [...x, ...next].slice(0, 4));
  }

  const boardOrder = boards.filter((b) => picked.has(b.page)).map((b) => b.page);

  async function run() {
    if (presenting && !boardOrder.length) { setErr("Attach the boards you want presented — that's the design."); return; }
    setBusy(true); setErr(null);

    try {
      /* Boards go up as images, one per board, so the assistant can say WHICH
         board an option is on. Vision tops out near 1568px on the long edge. */
      let boardFiles: { name: string; dataUri: string; page: number; label: string }[] = [];
      /* Full-resolution board per page, and the rectangles each one divides into.
         The model gets a NUMBERED copy so it can name a drawing without ever
         having to invent coordinates. */
      const fullBoard = new Map<number, string>();
      const cellsOf = new Map<number, Cell[]>();
      if (presenting && boardFile) {
        for (let i = 0; i < boardOrder.length; i++) {
          const n = boardOrder[i];
          setStage(`Reading board ${i + 1} of ${boardOrder.length} and finding the drawings on it…`);
          const full = await renderPageAt(boardFile, n, 3400, 0.94);
          fullBoard.set(n, full);
          let cells: Cell[] = [];
          try { cells = await detectCells(full); } catch { cells = []; }
          cellsOf.set(n, cells);
          boardFiles.push({
            name: boardFile.name, page: n,
            label: `Board ${i + 1} of ${boardOrder.length} — ${cells.length} numbered drawing${cells.length === 1 ? "" : "s"}`,
            dataUri: cells.length > 1 ? await annotateCells(full, cells, 1500) : await renderPageAt(boardFile, n, 1500, 0.82),
          });
        }
      }

      setStage(presenting ? "Reading the drawings and writing the narration…" : "Writing the concept…");
      const res = await draftConcept(conceptContext(p), {
        options: presenting ? 0 : count, instruction, studio: STUDIO.name, mode,
        files: [...boardFiles, ...docs.map((d) => ({ name: d.name, dataUri: d.dataUri }))],
      });
      if (!res.ok) {
        setErr(res.reason === "no-key" ? "The assistant isn't switched on (needs an AI key)."
          : res.reason === "no-context" ? "Nothing to work from — attach the boards, or fill in the brief."
          : `Couldn't reach the assistant${res.reason ? ` (${res.reason})` : ""}.`);
        setBusy(false); setStage(null);
        return;
      }

      /* Presentation-quality renders, stored server-side so the workspace JSON
         doesn't carry megabytes of base64. */
      const boardImage = new Map<number, string>();
      const snip = new Map<string, string>();          // "board:cell" → image url
      /* Drawings go to R2 as real files. The small image store has a hard size
         cap that would force line work down to mush. Falls back gracefully. */
      const put = async (uri: string, name: string) => {
        try {
          const blob = await (await fetch(uri)).blob();
          const { url } = await storeFile(new File([blob], name, { type: blob.type || 'image/jpeg' }), p.client?.code);
          return url;
        } catch { /* R2 unavailable — try the image store */ }
        try { return await storeImage(uri, p.client?.code); } catch { return uri; }
      };
      if (presenting && boardFile) {
        for (let i = 0; i < boardOrder.length; i++) {
          const n = boardOrder[i];
          setStage(`Cutting the drawings out of board ${i + 1} of ${boardOrder.length}…`);
          const full = fullBoard.get(n) ?? await renderPageAt(boardFile, n, 3400, 0.94);
          boardImage.set(n, await put(full, `board-${i + 1}.jpg`));
          /* Only the rectangles the assistant said were worth showing. */
          const cells = cellsOf.get(n) ?? [];
          const wanted = new Set(
            (res.panels ?? [])
              .filter((x) => boardOrder[(x.board ?? 0) - 1] === n && x.worthShowing)
              .map((x) => x.cell),
          );
          for (const cell of cells) {
            if (!wanted.has(cell.n)) continue;
            snip.set(`${i + 1}:${cell.n}`, await put(await cropCell(full, cell, 2400, 0.004, 0.94), `board-${i + 1}-${cell.n}.jpg`));
          }
        }
      }
      /* The assistant reports boards by their position in what it was given. */
      const pageOf = (reported?: number | null) => {
        if (!reported) return undefined;
        return boardOrder[reported - 1] ?? (boardOrder.includes(reported) ? reported : undefined);
      };

      const cover = p.deck?.slides.find((s) => s.kind === "cover");
      const filmSlide = p.deck?.slides.find((s) => s.kind === "film");
      const slides = [
        ...(cover ? [cover] : []),
        ...(filmSlide ? [filmSlide] : []),
        ...(res.slides ?? []).map((s) => {
          /* A slide that named a drawing gets that drawing — the snip, not the
             whole sheet. Falling back to the board is better than no image. */
          const img = s.useBoard
            ? (s.useCell ? snip.get(`${s.useBoard}:${s.useCell}`) : undefined)
              ?? boardImage.get(boardOrder[s.useBoard - 1])
            : undefined;
          const caption = s.useBoard && s.useCell
            ? (res.panels ?? []).find((x) => x.board === s.useBoard && x.cell === s.useCell)?.caption
            : undefined;
          return {
            id: `sl-${Math.random().toString(36).slice(2, 9)}`,
            kind: img ? ("image" as const) : s.kind,
            title: s.title, body: s.body + (caption ? `\n\n${caption}` : ""),
            images: img ? [img] : undefined,
            layout: (img ? "full" : "split") as "full" | "split", aiDrafted: true,
            ...(s.sourceQuote ? { source: { quote: s.sourceQuote, where: s.sourceWhere ?? undefined } } : {}),
          };
        }),
      ];
      const options = (res.options ?? []).map((o) => {
        const page = pageOf(o.page);
        const img = page != null ? boardImage.get(page) : undefined;
        return {
          id: `opt-${Math.random().toString(36).slice(2, 9)}`,
          ref: o.ref || "?", name: o.name?.trim() || `Option ${o.ref}`, thesis: o.thesis,
          description: o.description, images: img ? [img] : [],
          strengths: o.strengths ?? [], tradeoffs: o.tradeoffs ?? [],
          costNote: o.costNote ?? undefined,
          boardRef: o.boardRef ?? undefined, page,
          metrics: (o.metrics ?? []).filter((m) => m.label && m.value),
          decisions: o.decisions ?? [],
        };
      });
      const sources = (res.sources ?? []).filter((s) => s.quote?.trim()).map((s) => ({
        id: `src-${Math.random().toString(36).slice(2, 9)}`, quote: s.quote.trim(),
        where: s.where ?? undefined, topic: s.topic, usedFor: s.usedFor ?? undefined, file: s.file ?? undefined,
      }));

      actions.setDeck(p.id, {
        title: res.title, subtitle: res.subtitle ?? undefined, slides, options, mode,
        ...(sources.length ? { sources } : {}),
        ...(res.readFiles?.length ? { readFiles: res.readFiles } : {}),
      }, by);
      if (res.ignored) console.info("[concept] left out of the narrative:", res.ignored);
      onClose();
    } catch (e: any) {
      setErr(`Something went wrong preparing the boards${e?.message ? ` — ${e.message}` : ""}.`);
    } finally {
      setBusy(false); setStage(null);
    }
  }

  return (
    <Modal open onClose={busy ? () => {} : onClose} title={isProposal ? "Draft the design proposal" : presenting ? "Present the design" : "Draft the concept with AI"} wide>
      <div className="col" style={{ gap: 14 }}>
        {/* which job this is */}
        {!isProposal && <div className="row wrap" style={{ gap: 6 }}>
          {([["present", "Present a design I've drawn"], ["develop", "Help me develop one"]] as const).map(([k, label]) => (
            <button key={k} className="chip" disabled={busy} onClick={() => setMode(k)}
              style={{ fontSize: 12.5, padding: "6px 13px", background: mode === k ? "var(--accent)" : "var(--card)", color: mode === k ? "#fff" : "var(--ink-2)", borderColor: mode === k ? "var(--accent)" : "var(--line-2)" }}>
              {label}
            </button>
          ))}
        </div>}

        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          {isProposal
            ? "Attach the drawings of the resolved scheme. The assistant reads them and writes the proposal — what the building does, the approach and arrival, then through it space by space, the materials, and what happens next. It shows each drawing beside the part of the walkthrough it belongs to, and it will not invent anything the drawings don't show."
            : presenting
            ? "Your boards are the design. The assistant reads them and writes the presentation around them — what each option does, the figures printed on the sheet, and the decisions the drawing embodies. It will not invent an option you didn't draw or a number you didn't print."
            : "There's no design yet. The assistant reads the brief, accommodation, site, budget and mood board and proposes genuinely different approaches for you to react to — a starting point, not a scheme."}
        </p>

        {presenting ? (
          <div className="field">
            <label>The boards <span className="dim">— the massing options as you drew them</span></label>
            {boards.length === 0 ? (
              <div className="panel" style={{ padding: "14px 15px" }}>
                <label className="btn accent sm" style={{ cursor: busy ? "default" : "pointer" }}>
                  <Icon.folder width={13} />Attach the drawing set
                  <input type="file" accept="application/pdf" hidden disabled={busy} onChange={(e) => { void takeBoards(e.target.files?.[0] ?? null); e.currentTarget.value = ""; }} />
                </label>
                <div className="dim" style={{ fontSize: 11.5, marginTop: 9, lineHeight: 1.55 }}>
                  A PDF of the option boards, any size — it is opened and rendered here on your machine, so nothing large is
                  uploaded. Each page becomes a board at full resolution: the client sees your drawing, not a description
                  of it.
                </div>
              </div>
            ) : (
              <div className="panel" style={{ padding: "12px 13px" }}>
                <div className="row between wrap" style={{ gap: 10, marginBottom: 10 }}>
                  <span className="row" style={{ gap: 8, minWidth: 0 }}>
                    <Icon.brief width={14} style={{ color: "var(--accent-ink)", flexShrink: 0 }} />
                    <span style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{boardFile?.name}</span>
                    <span className="dim" style={{ fontSize: 11.5, flexShrink: 0 }}>{boards.length} page{boards.length === 1 ? "" : "s"} · {picked.size} to present</span>
                  </span>
                  <span className="row" style={{ gap: 6 }}>
                    <button className="chip" style={{ fontSize: 11 }} disabled={busy} onClick={() => setPicked(new Set(boards.map((b) => b.page)))}>All</button>
                    <button className="chip" style={{ fontSize: 11 }} disabled={busy} onClick={() => setPicked(new Set())}>None</button>
                    <button className="iconbtn" style={{ width: 24, height: 24 }} disabled={busy} title="Remove" onClick={() => { setBoards([]); setBoardFile(null); setPicked(new Set()); }}><Icon.close width={11} /></button>
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 8 }}>
                  {boards.map((b) => {
                    const on = picked.has(b.page);
                    return (
                      <button key={b.page} disabled={busy}
                        onClick={() => setPicked((s) => { const n = new Set(s); n.has(b.page) ? n.delete(b.page) : n.add(b.page); return n; })}
                        style={{ padding: 0, border: on ? "2px solid var(--accent)" : "1px solid var(--line-2)", borderRadius: 7, overflow: "hidden", cursor: "pointer", background: "#fff", position: "relative", lineHeight: 0, opacity: on ? 1 : 0.5 }}>
                        <img src={b.thumb} alt={`Page ${b.page}`} style={{ width: "100%", display: "block" }} />
                        <span style={{ position: "absolute", top: 4, left: 5, fontSize: 9.5, letterSpacing: 0.6, textTransform: "uppercase", background: on ? "var(--accent)" : "rgba(255,255,255,.9)", color: on ? "#fff" : "var(--ink-2)", borderRadius: 4, padding: "1px 5px" }}>
                          {on ? `Board ${boardOrder.indexOf(b.page) + 1}` : `p${b.page}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
                  Deselect anything that isn't an option board — a cover sheet or a blank page will otherwise be read as a design.
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="field"><label>How many options</label>
            <div className="row wrap" style={{ gap: 6 }}>
              {[2, 3, 4].map((n) => <button key={n} className="chip" style={{ fontSize: 12, background: count === n ? "var(--accent)" : "var(--card)", color: count === n ? "#fff" : "var(--ink-2)", borderColor: count === n ? "var(--accent)" : "var(--line-2)" }} onClick={() => setCount(n)}>{n} options</button>)}
              <button className="chip" style={{ fontSize: 12, background: count === 0 ? "var(--accent)" : "var(--card)", color: count === 0 ? "#fff" : "var(--ink-2)", borderColor: count === 0 ? "var(--accent)" : "var(--line-2)" }} onClick={() => setCount(0)}>Narrative only</button>
            </div>
          </div>
        )}

        {/* supporting reading, either way */}
        <div className="field">
          <label>{presenting ? "Anything else it should read" : "The client's proposal or brief"} <span className="dim">— PDF, scan or photographed pages</span></label>
          <div className="panel" style={{ padding: docs.length ? "10px 12px" : "12px 13px" }}>
            {docs.length > 0 && (
              <div className="col" style={{ gap: 6, marginBottom: 10 }}>
                {docs.map((d, i) => (
                  <div key={i} className="row between" style={{ gap: 10 }}>
                    <span className="row" style={{ gap: 8, minWidth: 0 }}>
                      <Icon.brief width={14} style={{ color: "var(--accent-ink)", flexShrink: 0 }} />
                      <span style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
                      <span className="dim" style={{ fontSize: 11, flexShrink: 0 }}>{(d.size / 1024 / 1024).toFixed(1)} MB</span>
                    </span>
                    <button className="iconbtn" style={{ width: 24, height: 24 }} disabled={busy} onClick={() => setDocs((x) => x.filter((_, j) => j !== i))}><Icon.close width={11} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="row wrap" style={{ gap: 10, alignItems: "center" }}>
              <label className="btn ghost sm" style={{ cursor: busy ? "default" : "pointer" }}>
                <Icon.folder width={13} />{docs.length ? "Attach another" : "Attach a document"}
                <input type="file" accept="application/pdf,image/*,.txt,.md,.csv" multiple hidden disabled={busy} onChange={(e) => { void addDocs(e.target.files); e.currentTarget.value = ""; }} />
              </label>
              <span className="dim" style={{ fontSize: 11.5, lineHeight: 1.5, flex: 1, minWidth: 180 }}>
                The client's brief or proposal, if you have one. It keeps only what belongs in a concept and quotes what it
                used — fees, dates and contract terms are left out.
              </span>
            </div>
          </div>
        </div>

        {hasDeck && (
          <div className="panel" style={{ padding: "10px 13px", borderLeft: "3px solid var(--warn)" }}>
            <b style={{ fontSize: 13 }}>This replaces the current narrative.</b>
            <div className="dim" style={{ fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>Your cover and film slides, and any client responses to options, are kept. Slides you've written will be replaced.</div>
          </div>
        )}

        <div className="field"><label>Your direction <span className="dim">(optional, but it shows)</span></label>
          <textarea className="input" rows={3} value={instruction} onChange={(e) => setInstruction(e.target.value)}
            placeholder={presenting
              ? "e.g. Option 1 is the one we're recommending — say so. The curve is about pulling customers off the diagonal road, not about looks."
              : "e.g. Lead with the courtyard idea. Keep it modest — they were nervous about looking showy."} />
        </div>

        {err && <div style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}
        {busy && <div className="row" style={{ gap: 8, color: "var(--ink-3)", fontSize: 13 }}><span className="dot" />{stage ?? "Working…"}</div>}
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={busy || (presenting && !boardOrder.length)} onClick={run}>
            <Icon.spark width={15} />{busy ? "Working…" : presenting ? `Present ${boardOrder.length || ""} board${boardOrder.length === 1 ? "" : "s"}`.replace("  ", " ") : "Draft it"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ---- edit one slide ---- */
function SlideModal({ p, slide, onClose }: { p: Project; slide: DeckSlide; onClose: () => void }) {
  const [title, setTitle] = useState(slide.title);
  const [body, setBody] = useState(slide.body ?? "");
  const [images, setImages] = useState<string[]>(slide.images ?? []);
  const [layout, setLayout] = useState(slide.layout ?? "split");
  const [busy, setBusy] = useState(false);
  const [rw, setRw] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const library = [
    ...(p.moodboard?.images ?? []).map((m: any) => m.url).filter(Boolean),
    ...(p.concepts ?? []).map((c: any) => c.url).filter(Boolean),
    ...(p.deliverables ?? []).filter((d) => d.previewUrl && !d.archived).slice(0, 12).map((d) => d.previewUrl!),
  ].filter((v, i, a) => a.indexOf(v) === i).slice(0, 24);

  async function upload(f: File | null) {
    if (!f) return;
    setBusy(true);
    try { const url = await storeImage(await fileToDataUri(f, 2000, 0.85), p.client?.code); setImages((x) => [...x, url]); } catch { /* image store off — keep the dialog usable */ }
    setBusy(false);
  }
  async function tighten() {
    setBusy(true); setErr(null);
    const res = await rewriteSlide({ title, body }, rw || "Tighten it. Same meaning, better writing.", conceptContext(p));
    setBusy(false);
    if (!res.ok) { setErr(res.reason === "no-key" ? "The assistant isn't switched on." : "Couldn't reach the assistant."); return; }
    if (res.title) setTitle(res.title);
    if (res.body) setBody(res.body);
  }

  return (
    <Modal open onClose={onClose} title={`${KIND_LABEL[slide.kind]} slide`} wide>
      <div className="col" style={{ gap: 14 }}>
        <div className="field"><label>Heading</label><input className="input" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} /></div>
        <div className="field"><label>What you'll say</label><textarea className="input" rows={6} value={body} onChange={(e) => setBody(e.target.value)} placeholder="The thinking, in your own words." /></div>

        <div className="panel" style={{ padding: "10px 13px" }}>
          <div className="row wrap" style={{ gap: 8, alignItems: "center" }}>
            <Icon.spark width={14} style={{ color: "var(--accent-ink)" }} />
            <input className="input" style={{ flex: 1, minWidth: 200, fontSize: 12.5, padding: "5px 9px" }} value={rw} onChange={(e) => setRw(e.target.value)} placeholder="Ask for a rewrite — 'warmer', 'shorter', 'lead with the courtyard'…" />
            <button className="btn ghost sm" disabled={busy} onClick={tighten}>{busy ? "Writing…" : "Rewrite"}</button>
          </div>
          <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>Keeps your facts and meaning — it improves the writing, not the design.</div>
        </div>
        {err && <div style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}

        <div className="field"><label>Layout</label>
          <div className="row wrap" style={{ gap: 6 }}>
            {(["split", "full", "grid"] as const).map((l) => (
              <button key={l} className={"chip" + (layout === l ? " on" : "")} style={{ fontSize: 12, textTransform: "capitalize", background: layout === l ? "var(--accent)" : "var(--card)", color: layout === l ? "#fff" : "var(--ink-2)", borderColor: layout === l ? "var(--accent)" : "var(--line-2)" }} onClick={() => setLayout(l)}>{l === "split" ? "Text & image" : l === "full" ? "Image full" : "Image grid"}</button>
            ))}
          </div>
        </div>

        {images.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(96px,1fr))", gap: 6 }}>
            {images.map((src, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img src={src} alt="" style={{ width: "100%", height: 68, objectFit: "cover", borderRadius: 6, border: "1px solid var(--line)" }} />
                <button className="iconbtn" style={{ position: "absolute", top: 3, right: 3, width: 20, height: 20, background: "rgba(255,255,255,.9)" }} onClick={() => setImages((x) => x.filter((_, j) => j !== i))}><Icon.close width={10} /></button>
              </div>
            ))}
          </div>
        )}

        <div className="row wrap" style={{ gap: 8 }}>
          <label className="btn ghost sm" style={{ cursor: "pointer" }}>
            <Icon.image width={13} />{busy ? "Uploading…" : "Upload an image"}
            <input type="file" accept="image/*" hidden disabled={busy} onChange={(e) => upload(e.target.files?.[0] ?? null)} />
          </label>
        </div>

        {library.length > 0 && (
          <div className="field"><label>Or pull from the project <span className="dim">— mood board, directions, drawings</span></label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(78px,1fr))", gap: 6, maxHeight: 170, overflowY: "auto" }}>
              {library.map((src, i) => (
                <button key={i} onClick={() => setImages((x) => x.includes(src) ? x : [...x, src])} style={{ padding: 0, border: images.includes(src) ? "2px solid var(--accent)" : "1px solid var(--line)", borderRadius: 6, overflow: "hidden", cursor: "pointer", background: "none", lineHeight: 0 }}>
                  <img src={src} alt="" style={{ width: "100%", height: 56, objectFit: "cover", display: "block" }} />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" onClick={() => { actions.updateSlide(p.id, slide.id, { title, body, images, layout }); onClose(); }}><Icon.check width={15} />Save slide</button>
        </div>
      </div>
    </Modal>
  );
}

/* ---- edit one option ---- */
function OptionModal({ p, option, onClose }: { p: Project; option: DesignOption; onClose: () => void }) {
  const [name, setName] = useState(option.name);
  const [thesis, setThesis] = useState(option.thesis);
  const [description, setDescription] = useState(option.description);
  const [strengths, setStrengths] = useState(option.strengths.join("\n"));
  const [tradeoffs, setTradeoffs] = useState(option.tradeoffs.join("\n"));
  const [costNote, setCostNote] = useState(option.costNote ?? "");
  const [images, setImages] = useState<string[]>(option.images);
  const [area, setArea] = useState(option.area != null ? String(option.area) : "");
  const [boardRef, setBoardRef] = useState(option.boardRef ?? "");
  const [decisions, setDecisions] = useState((option.decisions ?? []).join("\n"));
  const [metrics, setMetrics] = useState<{ label: string; value: string }[]>(option.metrics ?? []);
  const [busy, setBusy] = useState(false);
  const unit = p.giaUnit === "sqm" ? "m²" : "ft²";

  async function upload(f: File | null) {
    if (!f) return;
    setBusy(true);
    try { const url = await storeImage(await fileToDataUri(f, 2000, 0.85), p.client?.code); setImages((x) => [...x, url]); } catch { /* image store off — keep the dialog usable */ }
    setBusy(false);
  }
  const noTradeoffs = !tradeoffs.trim();

  return (
    <Modal open onClose={onClose} title={`Option ${option.ref}`} wide>
      <div className="col" style={{ gap: 14 }}>
        <div className="row wrap" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1, minWidth: 180 }}><label>Name</label><input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. The Courtyard House" /></div>
          <div className="field" style={{ width: 84 }}><label>Ref</label><input className="input mono" value={option.ref} readOnly style={{ textAlign: "center" }} /></div>
          <div className="field" style={{ width: 108 }}><label>Sheet</label><input className="input mono" style={{ fontSize: 12.5 }} value={boardRef} onChange={(e) => setBoardRef(e.target.value)} placeholder="A101" /></div>
          <div className="field" style={{ width: 150 }}><label>Area <span className="dim">({unit})</span></label>
            <input className="input mono" inputMode="numeric" value={area} onChange={(e) => setArea(e.target.value.replace(/[^\d.]/g, ""))} placeholder="48859" />
          </div>
        </div>
        <div className="field"><label>The idea, in one sentence</label><input className="input" value={thesis} onChange={(e) => setThesis(e.target.value)} placeholder="What this approach actually does." /></div>
        <div className="field"><label>How it works</label><textarea className="input" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
        <div className="field"><label>Design decisions <span className="dim">— one per line; this is the narration the client hears</span></label>
          <textarea className="input" rows={4} value={decisions} onChange={(e) => setDecisions(e.target.value)}
            placeholder={"The curve pulls the retail frontage towards the diagonal road.\nThe truck court is kept behind the mass, out of the customer's view."} />
        </div>

        {/* figures as printed on the board — editable, because a misread number is worse than none */}
        <div className="field">
          <label>Figures on the sheet <span className="dim">— exactly as printed</span></label>
          <div className="col" style={{ gap: 6 }}>
            {metrics.map((m, i) => (
              <div key={i} className="row" style={{ gap: 6 }}>
                <input className="input" style={{ flex: 1, fontSize: 12.5 }} value={m.label} placeholder="Foot Print Area"
                  onChange={(e) => setMetrics((x) => x.map((y, j) => j === i ? { ...y, label: e.target.value } : y))} />
                <input className="input mono" style={{ width: 150, fontSize: 12.5 }} value={m.value} placeholder="48,859 sq. ft."
                  onChange={(e) => setMetrics((x) => x.map((y, j) => j === i ? { ...y, value: e.target.value } : y))} />
                <button className="iconbtn" style={{ width: 30, height: 30 }} onClick={() => setMetrics((x) => x.filter((_, j) => j !== i))}><Icon.close width={11} /></button>
              </div>
            ))}
            <button className="chip" style={{ fontSize: 11, alignSelf: "flex-start" }} onClick={() => setMetrics((x) => [...x, { label: "", value: "" }])}><Icon.plus width={11} />Add a figure</button>
          </div>
        </div>
        <div className="row wrap" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1, minWidth: 220 }}><label>Strengths <span className="dim">— one per line</span></label><textarea className="input" rows={4} value={strengths} onChange={(e) => setStrengths(e.target.value)} /></div>
          <div className="field" style={{ flex: 1, minWidth: 220 }}><label>Trade-offs <span className="dim">— one per line</span></label><textarea className="input" rows={4} value={tradeoffs} onChange={(e) => setTradeoffs(e.target.value)} placeholder="What this option gives up." /></div>
        </div>
        {noTradeoffs && (
          <div className="panel" style={{ padding: "9px 12px", borderLeft: "3px solid var(--warn)", fontSize: 12.5, lineHeight: 1.55 }}>
            No trade-offs listed. Every approach gives something up — if the client finds out later rather than now, it costs you trust.
          </div>
        )}
        <div className="field"><label>Cost implication <span className="dim">— in words, relative to the others</span></label><input className="input" value={costNote} onChange={(e) => setCostNote(e.target.value)} placeholder="e.g. The most economical of the three." /></div>

        {images.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(110px,1fr))", gap: 6 }}>
            {images.map((src, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img src={src} alt="" style={{ width: "100%", height: 76, objectFit: "cover", borderRadius: 6, border: "1px solid var(--line)" }} />
                <button className="iconbtn" style={{ position: "absolute", top: 3, right: 3, width: 20, height: 20, background: "rgba(255,255,255,.9)" }} onClick={() => setImages((x) => x.filter((_, j) => j !== i))}><Icon.close width={10} /></button>
              </div>
            ))}
          </div>
        )}
        <label className="btn ghost sm" style={{ cursor: "pointer", alignSelf: "flex-start" }}>
          <Icon.image width={13} />{busy ? "Uploading…" : "Add an image"}
          <input type="file" accept="image/*" hidden disabled={busy} onChange={(e) => upload(e.target.files?.[0] ?? null)} />
        </label>

        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!name.trim()} onClick={() => {
            const n = parseFloat(area);
            actions.updateOption(p.id, option.id, {
              name, thesis, description, images, costNote: costNote.trim() || undefined,
              strengths: strengths.split("\n").map((s) => s.trim()).filter(Boolean),
              tradeoffs: tradeoffs.split("\n").map((s) => s.trim()).filter(Boolean),
              decisions: decisions.split("\n").map((s) => s.trim()).filter(Boolean),
              metrics: metrics.filter((m) => m.label.trim() && m.value.trim()),
              area: Number.isFinite(n) && n > 0 ? n : undefined,
              boardRef: boardRef.trim() || undefined,
            });
            onClose();
          }}><Icon.check width={15} />Save option</button>
        </div>
      </div>
    </Modal>
  );
}

/* ---- full-screen present mode ---- */
export function Presenter({ p, start, onClose }: { p: Project; start: number; onClose: () => void }) {
  const deck = p.deck!;
  const [i, setI] = useState(start);
  /* The chrome gets out of the way while the film runs — a header, a counter and
     two buttons all moving in the corner of your eye is noise around the work. */
  const [idle, setIdle] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const slide = deck.slides[i];

  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if (e.key === "Escape") { if (!document.fullscreenElement) onClose(); return; }
      if (e.key === "ArrowRight" || e.key === " ") setI((x) => Math.min(deck.slides.length - 1, x + 1));
      if (e.key === "ArrowLeft") setI((x) => Math.max(0, x - 1));
    };
    window.addEventListener("keydown", f);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", f); document.body.style.overflow = ""; };
  }, [deck.slides.length, onClose]);

  useEffect(() => {
    let t: number | undefined;
    const wake = () => { setIdle(false); window.clearTimeout(t); t = window.setTimeout(() => setIdle(true), 2600); };
    wake();
    window.addEventListener("mousemove", wake);
    window.addEventListener("keydown", wake);
    return () => { window.clearTimeout(t); window.removeEventListener("mousemove", wake); window.removeEventListener("keydown", wake); };
  }, [i]);

  if (!slide) return null;
  const opts = deck.options;

  return createPortal(
    <div ref={rootRef} style={{ position: "fixed", inset: 0, zIndex: 200, background: "#131009", color: "#f6f4ef", display: "flex", flexDirection: "column" }}>
      <div className="row between" style={{ padding: "12px 22px", borderBottom: "1px solid rgba(255,255,255,.12)", flexShrink: 0, opacity: idle ? 0 : 1, transition: "opacity .4s", pointerEvents: idle ? "none" : "auto" }}>
        <span style={{ fontSize: 13, opacity: .75 }}>{deck.title}</span>
        <span className="row" style={{ gap: 10 }}>
          <span className="mono" style={{ fontSize: 12, opacity: .6 }}>{i + 1} / {deck.slides.length}</span>
          <button className="iconbtn" style={{ width: 30, height: 30, background: "transparent", border: "1px solid rgba(255,255,255,.2)", color: "#f6f4ef" }} title="Full screen" onClick={() => { if (!document.fullscreenElement) rootRef.current?.requestFullscreen?.().catch(() => {}); else document.exitFullscreen?.().catch(() => {}); }}><Icon.expand width={15} /></button>
          <button className="iconbtn" style={{ width: 30, height: 30, background: "transparent", border: "1px solid rgba(255,255,255,.2)", color: "#f6f4ef" }} onClick={onClose}><Icon.close width={15} /></button>
        </span>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "clamp(24px,5vw,64px)", display: "grid", placeItems: "center" }}>
        <div style={{ width: "100%", maxWidth: 1100 }}>
          {slide.video ? (
            <div>
              <video src={slide.video} controls playsInline preload="metadata"
                style={{ width: "100%", maxHeight: "72vh", borderRadius: 10, background: "#000", display: "block" }} />
              <h2 className="serif" style={{ fontSize: "clamp(20px,2.4vw,30px)", marginTop: 16 }}>{slide.title}</h2>
              {slide.body && <p style={{ fontSize: 15, opacity: .74, lineHeight: 1.7, marginTop: 8, maxWidth: 820 }}>{slide.body}</p>}
            </div>
          ) : slide.kind === "film" ? (
            (() => {
              const film = (p.films ?? []).find((f) => f.id === slide.filmId);
              if (!film) return <div style={{ opacity: .6, fontSize: 15 }}>The film for this slide is no longer in the project.</div>;
              return (
                <div>
                  <div style={{ borderRadius: 12, overflow: "hidden" }}>
                    <FilmFrame film={film} height="min(66vh, 620px)" theatre={false} />
                  </div>
                  <h2 className="serif" style={{ fontSize: "clamp(20px,2.4vw,28px)", marginTop: 18 }}>{slide.title}</h2>
                  {slide.body && <p style={{ fontSize: 14.5, opacity: .74, lineHeight: 1.7, marginTop: 8, maxWidth: 760 }}>{slide.body}</p>}
                </div>
              );
            })()
          ) : slide.kind === "cover" ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", opacity: .55 }}>{STUDIO.name}</div>
              <h1 className="serif" style={{ fontSize: "clamp(34px,5vw,62px)", marginTop: 18, lineHeight: 1.08 }}>{slide.title}</h1>
              {deck.subtitle && <div style={{ fontSize: 17, opacity: .6, marginTop: 12 }}>{deck.subtitle}</div>}
              {slide.body && <p style={{ fontSize: 15, opacity: .7, marginTop: 22, maxWidth: 560, marginInline: "auto", lineHeight: 1.7 }}>{slide.body}</p>}
            </div>
          ) : slide.kind === "options" && opts.length ? (
            <div>
              <h2 className="serif" style={{ fontSize: "clamp(26px,3.4vw,40px)", marginBottom: 8 }}>{slide.title}</h2>
              {slide.body && <p style={{ fontSize: 15, opacity: .72, lineHeight: 1.7, marginBottom: 24, maxWidth: 760 }}>{slide.body}</p>}
              <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit,minmax(260px,1fr))`, gap: 18 }}>
                {opts.map((o) => (
                  <div key={o.id} style={{ border: "1px solid rgba(255,255,255,.16)", borderRadius: 14, overflow: "hidden" }}>
                    {o.images[0] && <img src={o.images[0]} alt="" style={{ width: "100%", height: 150, objectFit: "cover", display: "block" }} />}
                    <div style={{ padding: "14px 16px" }}>
                      <div style={{ fontSize: 11, letterSpacing: 2, opacity: .5, textTransform: "uppercase" }}>Option {o.ref}</div>
                      <div className="serif" style={{ fontSize: 21, marginTop: 4 }}>{o.name}</div>
                      {o.thesis && <div style={{ fontSize: 13.5, opacity: .8, marginTop: 8, fontStyle: "italic", lineHeight: 1.55 }}>{o.thesis}</div>}
                      {o.tradeoffs.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase", opacity: .5 }}>Gives up</div>
                          <ul style={{ margin: "5px 0 0", paddingLeft: 16, fontSize: 12.5, opacity: .78, lineHeight: 1.6 }}>{o.tradeoffs.map((t, k) => <li key={k}>{t}</li>)}</ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : slide.layout === "full" && slide.images?.length ? (
            <div>
              <img src={slide.images[0]} alt="" style={{ width: "100%", maxHeight: "62vh", objectFit: "contain", borderRadius: 10 }} />
              <h2 className="serif" style={{ fontSize: "clamp(22px,2.8vw,32px)", marginTop: 22 }}>{slide.title}</h2>
              {slide.body && <p style={{ fontSize: 15, opacity: .74, lineHeight: 1.75, marginTop: 10, maxWidth: 820 }}>{slide.body}</p>}
            </div>
          ) : slide.layout === "grid" && (slide.images?.length ?? 0) > 1 ? (
            <div>
              <h2 className="serif" style={{ fontSize: "clamp(24px,3vw,36px)", marginBottom: 18 }}>{slide.title}</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
                {slide.images!.map((src, k) => <img key={k} src={src} alt="" style={{ width: "100%", height: 200, objectFit: "cover", borderRadius: 10 }} />)}
              </div>
              {slide.body && <p style={{ fontSize: 15, opacity: .74, lineHeight: 1.75, marginTop: 20, maxWidth: 820 }}>{slide.body}</p>}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: slide.images?.length ? "1fr 1fr" : "1fr", gap: "clamp(20px,4vw,48px)", alignItems: "center" }}>
              <div>
                <h2 className="serif" style={{ fontSize: "clamp(24px,3.2vw,40px)", lineHeight: 1.15 }}>{slide.title}</h2>
                {slide.body && <p style={{ fontSize: "clamp(14px,1.15vw,16px)", opacity: .78, lineHeight: 1.8, marginTop: 16, whiteSpace: "pre-wrap" }}>{slide.body}</p>}
              </div>
              {slide.images?.[0] && <img src={slide.images[0]} alt="" style={{ width: "100%", maxHeight: "64vh", objectFit: "cover", borderRadius: 12 }} />}
            </div>
          )}
        </div>
      </div>

      <div className="row between" style={{ padding: "12px 22px", borderTop: "1px solid rgba(255,255,255,.12)", flexShrink: 0, opacity: idle ? 0 : 1, transition: "opacity .4s", pointerEvents: idle ? "none" : "auto" }}>
        <button className="btn ghost sm" style={{ background: "transparent", borderColor: "rgba(255,255,255,.2)", color: "#f6f4ef" }} disabled={i === 0} onClick={() => setI((x) => x - 1)}><Icon.back width={14} />Back</button>
        <span style={{ fontSize: 11.5, opacity: .45 }}>← → to move · Esc to leave</span>
        {i >= deck.slides.length - 1 && deck.options.some((o) => o.images[0])
          ? <a className="btn ghost sm" href={deck.options.find((o) => o.images[0])!.images[0]} target="_blank" rel="noreferrer" style={{ background: "transparent", borderColor: "rgba(255,255,255,.2)", color: "#f6f4ef" }}>The sheets<Icon.arrowUpRight width={14} /></a>
          : <button className="btn ghost sm" style={{ background: "transparent", borderColor: "rgba(255,255,255,.2)", color: "#f6f4ef" }} disabled={i >= deck.slides.length - 1} onClick={() => setI((x) => x + 1)}>Next<Icon.arrow width={14} /></button>}
      </div>
    </div>, document.body);
}
